import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as teamsApi from '../../api/teams'
import * as linksApi from '../../api/links'
import { useUIStore } from '../../stores/uiStore'
import Button from '../common/Button'
import { XMarkIcon, UserGroupIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

/**
 * LinkTeamModal Component
 * Modal for linking teams to a trainer
 */
export default function LinkTeamModal({ trainer, isOpen, onClose, onSuccess }) {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTeamId, setSelectedTeamId] = useState(null)

  // Fetch all teams
  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams', { search: searchQuery }],
    queryFn: () => teamsApi.getTeams({ search: searchQuery, limit: 50 }),
    enabled: isOpen,
  })

  // Link team mutation
  const linkMutation = useMutation({
    mutationFn: ({ trainerId, teamId }) => 
      linksApi.linkTrainerTeam(trainerId, teamId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['trainer', trainer.id])
      queryClient.invalidateQueries(['teams'])
      showToast('Team linked successfully', 'success')
      if (onSuccess) onSuccess(data)
      handleClose()
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to link team', 'error')
    },
  })

  const handleClose = () => {
    setSearchQuery('')
    setSelectedTeamId(null)
    onClose()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedTeamId) {
      showToast('Please select a team', 'error')
      return
    }
    linkMutation.mutate({
      trainerId: trainer.id,
      teamId: selectedTeamId
    })
  }

  if (!isOpen) return null

  const teams = teamsData?.data || []
  const linkedTeamIds = trainer.teams?.map(t => t.id) || []
  const availableTeams = teams.filter(t => !linkedTeamIds.includes(t.id))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Link Team to Trainer</h2>
            <p className="text-sm text-gray-500 mt-1">
              {trainer.first_name} {trainer.last_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Teams
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by team name, category..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Team List */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Teams
              </label>
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading teams...</div>
              ) : availableTeams.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery ? 'No teams found matching your search' : 'All teams are already linked'}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-80 overflow-y-auto">
                  {availableTeams.map((team) => (
                    <label
                      key={team.id}
                      className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                        selectedTeamId === team.id
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="team"
                        value={team.id}
                        checked={selectedTeamId === team.id}
                        onChange={() => setSelectedTeamId(team.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <UserGroupIcon className="h-5 w-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {team.team_name}
                        </p>
                        {team.category && (
                          <p className="text-xs text-gray-500">Category: {team.category}</p>
                        )}
                        {team.uid && (
                          <p className="text-xs text-gray-400">{team.uid}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={linkMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={linkMutation.isPending}
              disabled={!selectedTeamId}
            >
              <UserGroupIcon className="h-4 w-4 mr-1" />
              Link Team
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}